import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import * as actions from '../../actions';
import TwoFactorAuth from './TwoFactorAuth';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IconButton } from '@/components/ui/custom/IconButton';
import { Button } from '@/components/ui/button';
import {
	Eye,
	EyeOff,
	ArrowRight,
	Loader2,
	CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const AUTH_THEME = {
	backgroundAsset: '/img/login-landscape-bg.png',
	panelGradient:
		'linear-gradient(145deg, rgba(9, 51, 89, 0.58), rgba(16, 104, 72, 0.42), rgba(39, 153, 106, 0.34))',
	pageBackground: 'bg-gradient-to-b from-slate-50 via-blue-50/40 to-white',
	cardClass: 'border-[#d8e5f8] shadow-xl shadow-slate-200/70',
	buttonClass: 'bg-[#111316] hover:bg-[#111316] hover:brightness-105',
	buttonEnhancementClass:
		'px-4 font-normal tracking-[-0.3px] leading-[150%] shadow-none hover:shadow-none hover:scale-[1.02] transition-transform duration-200',
	inputFocusClass:
		'border-slate-300 focus-visible:border-[#78b3df] focus-visible:ring-[#deedf8]',
	forgotPasswordClass:
		'text-[#1f63c7] hover:text-[#174f9d] font-medium hover:underline transition-colors',
	heading: 'Respond Faster. Win More Customers.',
	description:
		'Business messaging that helps your team convert more leads with less effort.',
	bodyFontFamily:
		'"Inter", "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
	headingFontFamily:
		'"Host Grotesk", "Inter", "Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

// Phones: a full-height sign-in screen without the card, edge-to-edge fields
// with 20px gutters, clear of the notch and the home indicator, and dark
// mode colours. Desktop keeps the card layout.
const PHONE_PAGE_CLASS = 'max-md:min-h-dvh max-md:dark:bg-none max-md:dark:bg-background';
const PHONE_COLUMN_CLASS =
	'max-md:items-start max-md:px-5 max-md:pt-[max(4.5rem,calc(var(--safe-top)+3rem))] max-md:pb-[max(1.5rem,var(--safe-bottom))]';
const PHONE_CARD_CLASS =
	'max-md:rounded-none max-md:border-0 max-md:bg-transparent max-md:p-0 max-md:shadow-none';
const PHONE_INPUT_CLASS = 'max-md:rounded-xl';
const PHONE_INPUT_DARK_CLASS =
	'max-md:dark:border-input max-md:dark:focus-visible:border-sky-500 max-md:dark:focus-visible:ring-sky-500/20';
const PHONE_BUTTON_CLASS =
	'max-md:h-[52px] max-md:rounded-xl max-md:text-[17px] max-md:font-semibold max-md:active:opacity-80 max-md:dark:bg-white max-md:dark:text-[#111316]';

const ensureWebsiteFontsLoaded = () => {
	if (document.getElementById('chekkit-website-fonts')) {
		return;
	}

	const link = document.createElement('link');
	link.id = 'chekkit-website-fonts';
	link.rel = 'stylesheet';
	link.href =
		'https://fonts.googleapis.com/css2?family=Host+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap';
	document.head.appendChild(link);
};

const Login = () => {
	const dispatch = useDispatch();
	const isMobile = useIsMobile();
	const { error } = useSelector((state) => ({
		error: state.error,
	}));

	// Check if this is a whitelabeled deployment
	const isWhiteLabel =
		!window.location.hostname.includes('chekkit') &&
		!window.location.hostname.includes('localhost');

	const [state, setState] = useState({
		termsLocation: `${window.location.hostname}/terms-of-use`,
		privacyLocation: `${window.location.hostname}/privacy-policy`,
		showPassword: false,
		email: '',
		password: '',
		validationErrors: {
			username: '',
			password: '',
		},
		loaded: false,
		isTwoFactorAuthEnabled: false,
		_business: null,
		isLoading: false,
	});

	useEffect(() => {
		const initializeComponent = async () => {
			// Check if viewport meta tag already exists
			const existingViewport = document.querySelector(
				'meta[name="viewport"]'
			);
			if (!existingViewport) {
				const viewportMeta = document.createElement('meta');
				viewportMeta.name = 'viewport';
				viewportMeta.content = 'width=device-width, initial-scale=1.0';
				document.head.appendChild(viewportMeta);
			}

			document.title = 'Login | Reviews & Messaging';
			ensureWebsiteFontsLoaded();
			setState((prev) => ({ ...prev, loaded: true }));

			if (isWhiteLabel) {
				const res = await axios.post(
					'/api/terms-and-privacy-location',
					{
						hostname: window.location.hostname,
					}
				);

				if (
					res.data &&
					res.data.termsLocation &&
					res.data.privacyLocation
				) {
					setState((prev) => ({
						...prev,
						termsLocation: res.data.termsLocation,
						privacyLocation: res.data.privacyLocation,
					}));
				}
			}
		};

		const handleKeyPress = (event) => {
			if (event.key === 'Enter') {
				if (state.email && state.password) {
					handleSubmit(event);
				}
			}
		};

		initializeComponent();
		document.addEventListener('keydown', handleKeyPress);

		return () => {
			document.removeEventListener('keydown', handleKeyPress);
		};
	}, [state.email, state.password]);

	const renderError = () => {
		return (
			<div
				className={cn(
					error ? 'opacity-100' : 'opacity-0',
					'flex justify-center transition-all ease-in-out duration-300 min-h-5'
				)}
			>
				<div className="bg-red-50 border border-red-200 rounded-lg p-3 w-full max-md:rounded-xl max-md:dark:border-red-900/60 max-md:dark:bg-red-950/40">
					<p className="text-red-600 text-sm text-center font-medium max-md:dark:text-red-300">
						{error}
					</p>
				</div>
			</div>
		);
	};

	const handleClickShowPassword = (event) => {
		event.preventDefault(); // Prevent form submission
		setState((prev) => ({ ...prev, showPassword: !prev.showPassword }));
	};

	const handleInputChange = (event) => {
		const { name, value } = event.target;

		// Clear any existing validation errors when user starts typing
		const errors = { ...state.validationErrors };
		if (name === 'email') {
			errors.username = '';
		} else if (name === 'password') {
			errors.password = '';
		}

		setState((prev) => ({
			...prev,
			[name]: value,
			validationErrors: errors,
		}));
	};

	const logoSrc = () => (isWhiteLabel ? '/api/logo' : '/img/chekkit-new.png');
	const inputFocusClass = AUTH_THEME.inputFocusClass;
	const forgotPasswordClass = AUTH_THEME.forgotPasswordClass;

	const handleSubmit = async (event) => {
		if (event) {
			event.preventDefault();
		}

		const { email, password } = state;
		const errors = { username: '', password: '' };

		// Validate email
		if (!email) {
			errors.username = 'Email is required';
		} else if (!email.includes('@') || email.length < 5) {
			errors.username = 'Please enter a valid email address';
		}

		// Validate password
		if (!password) {
			errors.password = 'Password is required';
		}

		// If there are validation errors, show them and stop
		if (errors.username || errors.password) {
			setState((prev) => ({
				...prev,
				validationErrors: errors,
			}));
			return;
		}

		// If validation passes, proceed with login
		setState((prev) => ({ ...prev, isLoading: true }));
		const user = { email, password };
		const loginResponse = await dispatch(actions.loginUser(user));

		if (loginResponse.isTwoFactorAuthEnabled) {
			setState((prev) => ({
				...prev,
				isTwoFactorAuthEnabled: true,
				_business: loginResponse._business,
				isLoading: false,
			}));
		} else {
			setState((prev) => ({ ...prev, isLoading: false }));
		}
	};

	const renderUsernameAndPassword = () => {
		return (
			<form
				style={{
					width: '100%',
				}}
				onSubmit={handleSubmit}
			>
				<div className="grid gap-5">
					<div className="grid gap-2.5">
						<Label
							htmlFor="email"
							className="text-sm font-semibold text-foreground"
						>
							Email Address
						</Label>
						{state.loaded && (
							<Input
								id="email"
								name="email"
								tabIndex={1}
								autoFocus
								// The email keyboard and the saved-login pairing with the password below.
								inputMode="email"
								autoComplete="username"
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck={false}
								enterKeyHint="next"
								value={state.email}
								onChange={handleInputChange}
								placeholder="Enter your email"
								className={cn(
									'h-12 px-4 border-2 transition-all duration-200',
									'focus-visible:outline-none focus-visible:ring-4',
									state.validationErrors.username
										? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-100'
											: cn(inputFocusClass, PHONE_INPUT_DARK_CLASS),
									PHONE_INPUT_CLASS
								)}
								error={state.validationErrors.username}
								required
							/>
						)}
						{state.validationErrors.username && (
							<p className="text-xs text-red-500 font-medium">
								{state.validationErrors.username}
							</p>
						)}
					</div>

					<div className="grid gap-2.5">
						<div className="flex items-center justify-between">
							<Label
								htmlFor="password"
								className="text-sm font-semibold text-foreground"
							>
								Password
							</Label>
							<a
								href="/forgot-password"
								// Phones stay in the same tab, like an app's own screen.
								target={isMobile ? undefined : '_blank'}
								className={cn(
									'text-xs max-md:-my-3 max-md:py-3 max-md:text-sm max-md:dark:text-sky-400',
									forgotPasswordClass
								)}
							>
								Forgot password?
							</a>
						</div>
						{state.loaded && (
							<div className="relative">
								<Input
									id="password"
									tabIndex={2}
									name="password"
									type={
										state.showPassword ? 'text' : 'password'
									}
									autoComplete="current-password"
									enterKeyHint="go"
									placeholder="Enter your password"
									required
									value={state.password}
									onChange={handleInputChange}
									className={cn(
										'h-12 px-4 pr-12 border-2 transition-all duration-200',
										'focus-visible:outline-none focus-visible:ring-4',
										state.validationErrors.password
											? 'border-red-300 focus-visible:border-red-500 focus-visible:ring-red-100'
											: cn(inputFocusClass, PHONE_INPUT_DARK_CLASS),
										PHONE_INPUT_CLASS,
										'max-md:pr-14'
									)}
									error={state.validationErrors.password}
								/>
								<div className="absolute right-3 top-1/2 -translate-y-1/2 max-md:right-1">
									<IconButton
										className="hover:bg-muted text-muted-foreground hover:text-foreground transition-colors max-md:rounded-full"
										onClick={handleClickShowPassword}
										aria-label={
											state.showPassword
												? 'Hide password'
												: 'Show password'
										}
										aria-pressed={state.showPassword}
									>
										{state.showPassword ? (
											<Eye className="size-4" />
										) : (
											<EyeOff className="size-4" />
										)}
									</IconButton>
								</div>
							</div>
						)}
						{state.validationErrors.password && (
							<p className="text-xs text-red-500 font-medium">
								{state.validationErrors.password}
							</p>
						)}
					</div>

					<Button
						tabIndex={3}
						type="submit"
						disabled={state.isLoading}
						className={cn(
							'h-12',
							AUTH_THEME.buttonClass,
							'text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-200',
							'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg',
							'flex items-center justify-center gap-2 group',
							AUTH_THEME.buttonEnhancementClass,
							PHONE_BUTTON_CLASS
						)}
					>
						{state.isLoading ? (
							<>
								Log in
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							</>
						) : (
							<>
								Log in
								<ArrowRight className="size-4 group-hover:translate-x-1 transition-transform duration-200" />
							</>
						)}
					</Button>
				</div>
			</form>
		);
	};

	const renderTwoFactorAuthentication = () => {
		return (
			<TwoFactorAuth
				_business={state._business}
				handleSubmitWithLoginDetails={handleSubmit}
			/>
		);
	};

	return (
		<div
			className={cn(
				'min-h-screen',
				AUTH_THEME.pageBackground,
				PHONE_PAGE_CLASS
			)}
			style={{
				fontFamily:
					AUTH_THEME.bodyFontFamily ||
					'"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
			}}
		>
			<div className="min-h-screen lg:grid lg:grid-cols-2 max-md:min-h-dvh">
				{/* Left side - Login Form */}
				<div className={cn('flex items-center justify-center px-6 py-10 lg:px-12 lg:py-12', PHONE_COLUMN_CLASS)}>
					<div className="w-full max-w-md space-y-3 sm:space-y-4">
						{/* Logo */}
						<div className="text-center pt-1">
							<div className="flex justify-center">
								<img
									className={cn(
										isWhiteLabel
											? 'h-12 w-auto max-w-[240px]'
											: 'h-12 w-auto max-w-[260px] max-md:dark:invert'
									)}
									alt={
										isWhiteLabel
											? 'Company Logo'
											: 'Chekkit Logo'
									}
									src={logoSrc()}
								/>
							</div>
							{!state.isTwoFactorAuthEnabled && (
								<div className="md:hidden pt-7 pb-3">
									<h1 className="text-[28px] font-bold leading-tight tracking-tight text-foreground">
										Welcome back
									</h1>
									<p className="mt-1 text-[15px] text-muted-foreground">
										Log in to your account.
									</p>
								</div>
							)}
						</div>

						{/* Login Form */}
						<div
							className={cn(
								'bg-card rounded-2xl p-6 sm:p-8 border',
								AUTH_THEME.cardClass,
								PHONE_CARD_CLASS
							)}
						>
							{state.isTwoFactorAuthEnabled
								? renderTwoFactorAuthentication()
								: renderUsernameAndPassword()}

							{renderError()}
						</div>

						{/* Footer Links */}
						<div className="flex items-center justify-center space-x-2 pt-1 sm:pt-2">
							<a
								className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors max-md:inline-flex max-md:min-h-11 max-md:items-center max-md:text-[13px]"
								href={`https://${state.termsLocation}`}
							>
								Terms of Use
							</a>
							<div className="w-1 h-1 bg-muted-foreground rounded-full"></div>
							<a
								className="text-xs text-muted-foreground hover:text-foreground hover:underline transition-colors max-md:inline-flex max-md:min-h-11 max-md:items-center max-md:text-[13px]"
								href={`https://${state.privacyLocation}`}
							>
								Privacy Policy
							</a>
						</div>
					</div>
				</div>

				{/* Right side - Feature Showcase */}
				<div
					className="hidden lg:flex relative overflow-hidden bg-slate-700"
					style={{
						backgroundImage: `${AUTH_THEME.panelGradient}, url('${AUTH_THEME.backgroundAsset}')`,
						backgroundPosition: 'center',
						backgroundSize: 'cover',
					}}
				>
					<div className="absolute inset-0 bg-black/10"></div>

					{/* Content */}
					<div className="relative z-10 flex flex-col justify-center px-12 py-14 xl:px-14 xl:py-16 text-white">
						<div className="max-w-xl space-y-5">
							<h2
								className="text-4xl font-bold leading-tight"
								style={{
									fontFamily:
										AUTH_THEME.headingFontFamily ||
										AUTH_THEME.bodyFontFamily,
									letterSpacing: '-0.01em',
								}}
							>
								{isWhiteLabel
									? 'Supercharge your business'
									: AUTH_THEME.heading}
							</h2>
							<p className="text-xl text-slate-100/95 leading-relaxed">
								{AUTH_THEME.description}
							</p>

							<div className="space-y-2.5 pt-1 text-sm text-slate-100/95">
								<div className="flex items-center gap-2.5">
									<CheckCircle2 className="size-4 text-white" />
									One inbox for every channel
								</div>
								<div className="flex items-center gap-2.5">
									<CheckCircle2 className="size-4 text-white" />
									AI Employee coverage after hours
								</div>
								<div className="flex items-center gap-2.5">
									<CheckCircle2 className="size-4 text-white" />
									Missed-call text back automation
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Login;
